import posthog from 'posthog-js/dist/module.full.no-external'

import { logger } from '@/lib/observability/logger'

import { GEO_COUNTRY_META_NAME, GEO_REGION_META_NAME } from './geoMeta'

export interface GeoProperties {
  /** ISO 3166-1 alpha-2 country code (e.g. 'FR'), or null when unknown. */
  countryCode?: string | null
  /** ISO 3166-2 first-level subdivision code (e.g. 'CA'), or null when unknown. */
  regionCode?: string | null
}

/** Reads the geo codes the layout exposed as `<head>` meta tags. */
export function readGeoPropertiesFromMeta(): GeoProperties {
  const read = (name: string) =>
    document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || null

  return {
    countryCode: read(GEO_COUNTRY_META_NAME),
    regionCode: read(GEO_REGION_META_NAME)
  }
}

/**
 * Registers country/region as PostHog `$geoip_*` properties: we supply the
 * edge-resolved codes and set `$geoip_disable` so PostHog's IP-based enrichment
 * never overrides them and no IP-derived geo is stored. Only coarse ISO codes —
 * no IP, city, or lat/long.
 *
 * `$geoip_disable` is a persistent super property: a session rotation (30 min
 * idle) must never silently re-enable ingestion-side IP enrichment. The codes
 * themselves stay session-scoped like the iframe props, since geo belongs to
 * this page load rather than to the person — a rotation clears them, and
 * `registerSessionPropertiesOnRotation` (session.ts) re-registers them.
 *
 * Session properties outlive a reload, so each load fully reconciles them: a
 * code the edge no longer resolves is unregistered rather than left behind.
 */
export function registerGeoProperties({ countryCode, regionCode }: GeoProperties) {
  const properties: Record<string, string> = {}
  if (countryCode) properties.$geoip_country_code = countryCode
  if (regionCode) properties.$geoip_subdivision_1_code = regionCode

  try {
    posthog.register({ $geoip_disable: true })

    if (!countryCode) posthog.unregister_for_session('$geoip_country_code')
    if (!regionCode) posthog.unregister_for_session('$geoip_subdivision_1_code')

    posthog.register_for_session(properties)
  } catch {
    logger.warn('Failed to register geo analytics properties')
  }
}
